import { create } from 'zustand';
import { state as stateApi } from '../api/client';
import { defaultReleaseCal, type ReleaseCal } from '../lib/releases';
import type { CapMap } from '../lib/capacity';
import type { InitialPlanMap } from '../lib/columns';
import type { Snapshot } from '../lib/snapshots';

// Persistente Konfiguration aus dem Backend.
//
//   releaseCal      — Releases mit Start/Ende + Service Packs
//   capacityByTeam  — Kapazität pro Team × Release × Monat (in Phase 2.6
//                     editierbar im Modal; Phase 2.5 zeigt sie nur an)
//   initialPlan     — manueller Override für die Säulenplanung pro
//                     Release (oder bei team-mode pro Team::Release)
type ConfigState = {
  releaseCal: Record<string, ReleaseCal>;
  capacityByTeam: CapMap;
  initialPlan: InitialPlanMap;
  history: Snapshot[];

  loadFromBackend: () => Promise<void>;
  applyDefaults: () => Promise<void>;
  setInitialPlan: (initKey: string, stories: number, bugs: number) => Promise<void>;
  clearInitialPlan: (initKey: string) => Promise<void>;
  // Release-Kalender komplett ersetzen (atomic save vom Modal)
  setReleaseCal: (next: Record<string, ReleaseCal>) => Promise<void>;
  // Eine einzelne Capacity-Zelle setzen (Team × Release × Monat)
  setCapacityCell: (team: string, release: string, monthKey: string, stories: number, bugs: number) => Promise<void>;
  // Capacity komplett ersetzen (für Bulk-Bearbeitung)
  setCapacity: (next: CapMap) => Promise<void>;
  // History-Snapshot anhängen (wird beim CSV-Import gerufen)
  setHistory: (next: Snapshot[]) => Promise<void>;
};

export const useConfig = create<ConfigState>((set, get) => ({
  releaseCal: {},
  capacityByTeam: {},
  initialPlan: {},
  history: [],

  async loadFromBackend() {
    try {
      const snap = await stateApi.get();
      set({
        releaseCal:     (snap.releaseCal     ?? {}) as Record<string, ReleaseCal>,
        capacityByTeam: (snap.capacityByTeam ?? {}) as CapMap,
        initialPlan:    (snap.initialPlan    ?? {}) as InitialPlanMap,
        history:        (snap.history        ?? []) as Snapshot[],
      });
      await get().applyDefaults();
    } catch {
      // best effort – frontend lebt mit leerem State weiter
    }
  },

  // Mergt die Default-Releases (2026 R2 etc.) ohne Überschreiben.
  async applyDefaults() {
    const cur = get().releaseCal;
    const defaults = defaultReleaseCal();
    let changed = false;
    const next: Record<string, ReleaseCal> = { ...cur };
    for (const [name, cal] of Object.entries(defaults)) {
      if (!next[name] || (!next[name].start && !next[name].end)) {
        next[name] = cal;
        changed = true;
      }
    }
    if (changed) {
      set({ releaseCal: next });
      try { await stateApi.saveConfig({ releaseCal: next }); } catch { /* ignore */ }
    }
  },

  async setInitialPlan(initKey, stories, bugs) {
    const next: InitialPlanMap = { ...get().initialPlan };
    // Wenn beide Werte 0 sind → Eintrag löschen (= Default = Kapa)
    if ((stories || 0) <= 0 && (bugs || 0) <= 0) {
      delete next[initKey];
    } else {
      next[initKey] = { stories: Math.round(stories || 0), bugs: Math.round(bugs || 0) };
    }
    set({ initialPlan: next });
    try { await stateApi.saveConfig({ initialPlan: next }); } catch { /* ignore */ }
  },

  async clearInitialPlan(initKey) {
    const next: InitialPlanMap = { ...get().initialPlan };
    delete next[initKey];
    set({ initialPlan: next });
    try { await stateApi.saveConfig({ initialPlan: next }); } catch { /* ignore */ }
  },

  async setReleaseCal(next) {
    set({ releaseCal: next });
    try { await stateApi.saveConfig({ releaseCal: next }); } catch { /* ignore */ }
  },

  async setCapacityCell(team, release, monthKey, stories, bugs) {
    const cur = get().capacityByTeam;
    // Tief-Kopie nur entlang des Pfades, der sich ändert
    const next: CapMap = { ...cur };
    const teamMap     = next[team]               ? { ...next[team] }               : {};
    const releaseMap  = teamMap[release]         ? { ...teamMap[release] }         : {};
    const s = Math.max(0, Math.round(stories || 0));
    const b = Math.max(0, Math.round(bugs    || 0));
    if (s === 0 && b === 0) {
      delete releaseMap[monthKey];
    } else {
      releaseMap[monthKey] = { s, b };
    }
    if (Object.keys(releaseMap).length === 0) delete teamMap[release];
    else                                       teamMap[release] = releaseMap;
    if (Object.keys(teamMap).length === 0)     delete next[team];
    else                                        next[team] = teamMap;
    set({ capacityByTeam: next });
    try { await stateApi.saveConfig({ capacityByTeam: next }); } catch { /* ignore */ }
  },

  async setCapacity(next) {
    set({ capacityByTeam: next });
    try { await stateApi.saveConfig({ capacityByTeam: next }); } catch { /* ignore */ }
  },

  async setHistory(next) {
    set({ history: next });
    try { await stateApi.saveConfig({ history: next }); } catch { /* ignore */ }
  },
}));
